import { useState } from "react";
import { useDataStore } from "@store/user_org";
import api from "@services/api/client";
import { showNotification } from "context/notification.service";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import TextField from "@components/ui/textfields/TextField";
import AreaTextField from "@components/ui/textfields/AreaTextField";
import { SwitchBrick } from "@components/ui/selection/SwitchBrick";
import { Text1, Text2 } from "@components/ui/texts/Texts";

const DEFAULT_MESSAGES = {
  whatsapp:
    "Bonjour {client_name}, petit rappel : votre rendez-vous chez {org_name} pour {vehicle_info} est prévu le {reception_date}. À très bientôt !",
  email_subject: "Rappel : votre rendez-vous du {reception_date} — {org_name}",
};

const VARIABLES = [
  "{client_name}",
  "{vehicle_info}",
  "{org_name}",
  "{ordre_num}",
  "{reception_date}",
  "{tracking_url}",
];

type Config = {
  enabled: boolean;
  before_reception_hours: number;
  messages: Record<string, string>;
};

export function AppointmentReminders() {
  const orgData = useDataStore((s) => s.orgData);
  const orgId = orgData?.id;
  const fetchOrgData = useDataStore((s) => s.fetchOrgData);

  const existing: any = orgData?.config?.rdv_reminders || {};

  const [config, setConfig] = useState<Config>({
    enabled: existing.enabled !== false,
    before_reception_hours: existing.before_reception_hours ?? 24,
    messages: { ...DEFAULT_MESSAGES, ...(existing.messages || {}) },
  });

  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);
    try {
      await api.patch(`/organizations/${orgId}`, {
        config: { ...orgData?.config, rdv_reminders: config },
      });
      showNotification({ type: "success", message: "Configuration sauvegardée" });
      fetchOrgData(orgId);
    } catch {
      showNotification({ type: "error", message: "Erreur lors de la sauvegarde" });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig({
      enabled: true,
      before_reception_hours: 24,
      messages: { ...DEFAULT_MESSAGES },
    });
  };

  const updateNumber = (value: string) => {
    const num = parseInt(value, 10);
    if (!isNaN(num) && num > 0) {
      setConfig((c) => ({ ...c, before_reception_hours: num }));
    }
  };

  const updateMessage = (key: string, value: string) => {
    setConfig((c) => ({ ...c, messages: { ...c.messages, [key]: value } }));
  };

  return (
    <div className="flex flex-col gap-6 p-6 max-w-2xl">
      <Text1>Rappels RDV</Text1>

      <SwitchBrick
        checked={config.enabled}
        onChange={() => setConfig((c) => ({ ...c, enabled: !c.enabled }))}
        title="Rappel automatique de rendez-vous"
        description="Envoyer un rappel au client avant son rendez-vous"
      />

      {config.enabled && (
        <>
          <div className="border rounded-xl p-4 shadow-sm">
            <Text2>Planification</Text2>
            <div className="flex items-center gap-3 mt-3">
              <input
                type="number"
                min={1}
                value={config.before_reception_hours}
                onChange={(e) => updateNumber(e.target.value)}
                className="w-16 border rounded-xl px-2 py-2 text-h3 text-center shadow-sm focus:ring-4 focus:ring-neutral-300 outline-none transition"
              />
              <span className="text-h3 text-neutral-500">heures avant le rendez-vous</span>
            </div>
          </div>

          <div className="border rounded-xl p-4 shadow-sm">
            <Text2>Message WhatsApp</Text2>
            <div className="mt-3">
              <AreaTextField
                value={config.messages.whatsapp || ""}
                onChange={(v) => updateMessage("whatsapp", v)}
                rows={3}
              />
            </div>
          </div>

          <div className="border rounded-xl p-4 shadow-sm">
            <Text2>Objet email</Text2>
            <div className="mt-3">
              <TextField
                value={config.messages.email_subject || ""}
                onChange={(v) => updateMessage("email_subject", v)}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            <span className="text-h4 text-neutral-400">Variables disponibles :</span>
            {VARIABLES.map((v) => (
              <span key={v} className="text-h4 bg-neutral-100 px-2 py-0.5 rounded font-mono">
                {v}
              </span>
            ))}
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 border-t pt-4">
        <SecondaryButton label="Réinitialiser" onClick={handleReset} variant="small" />
        <Button
          label={saving ? "Enregistrement..." : "Enregistrer"}
          onClick={handleSave}
          disabled={saving}
          loading={saving}
          variant="small"
        />
      </div>
    </div>
  );
}
